import { memo, useRef, useState } from 'react';
import { CircleCheck, PinIcon } from 'lucide-react';

import { CloseCircle, Edit2, Menu, Setting2 } from 'iconsax-react';

import { Button } from '@src/design-system/button';
import { Checkbox } from '@src/design-system/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  DialogSub,
  DialogSubContent,
  DialogSubHeader,
  DialogSubTrigger,
  DialogClose,
} from '@src/design-system/dialog';
import { Input } from '@src/design-system/input';
import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';

import { cn } from '@src/design-system/helpers';
import { Label } from '@src/design-system/label';

import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/design-system/command';
import {
  Sortable,
  SortableContent,
  SortableItem,
  SortableItemHandle,
} from '@src/design-system/sortable';
import { useTableStates } from '@design-system/molecules/ticlick-table/hooks/use-table-states';

import type { Column } from '@tanstack/react-table';
import { toast } from 'react-hot-toast';
import viewApi from '../services/view-api';
import { Badge } from '@src/design-system/badge';
import { CreateOrEditViewForm } from './create-or-edit-view-form';
import { useGetColumnName } from '@src/shared/hooks/use-get-column-name';
import { TableActionIconButton } from '@src/design-system/molecules/ticlick-table/actions';

const useIsSettingDirty = () => {
  const { table } = useTiclickTableContext();

  const {
    isColumnPinningChanged,
    isColumnVisibilityChanged,
    isNamesChanged,
    isColumnOrderChanged,
  } = table.options.meta || {};

  const isDirty =
    isColumnPinningChanged ||
    isColumnVisibilityChanged ||
    isNamesChanged ||
    isColumnOrderChanged;

  return isDirty;
};

const Item = ({
  column,
  isMaximumPinned,
}: {
  column: Column<unknown>;
  isMaximumPinned: boolean;
}) => {
  const { columnNames, setColumnNames } = useTableStates();

  const [isEditMode, setIsEditMode] = useState(false);
  const [editName, setEditName] = useState('');

  const getColumnName = useGetColumnName();
  const { displayed, original, newName } = getColumnName(column);

  const columnId = column.columnDef?.id;

  const name = isEditMode ? editName : displayed;

  const isColumnRenamed = !!newName;

  const handleEnterEdit = () => {
    setEditName(displayed);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleSaveEdit = () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    if (!columnId) {
      toast.error('Column ID is required');
      return;
    }
    setColumnNames?.({
      ...columnNames,
      [columnId]: trimmed === original ? null : trimmed,
    });
    setIsEditMode(false);
  };

  if (isEditMode)
    return (
      <Input
        size={'sm'}
        value={editName}
        onChange={(e) => setEditName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSaveEdit();
        }}
        suffexIcon={
          <div className='text-black flex gap-1 items-center'>
            <CloseCircle
              onClick={handleCancelEdit}
              size={16}
              className='opacity-80 hover:opacity-100 cursor-pointer'
            />
            <CircleCheck
              onClick={handleSaveEdit}
              size={18}
              className='stroke-[1.5px] opacity-80 hover:opacity-100 cursor-pointer text-white fill-green-500 border-0'
            />
          </div>
        }
      />
    );

  return (
    <SortableItem
      value={column.id}
      asChild
    >
      <li className='flex items-center justify-between group'>
        <div className='flex items-center gap-2'>
          <SortableItemHandle
            autoFocus={false}
            asChild
          >
            <button>
              <Menu
                size={20}
                variant='Bold'
                className='text-primary-500 shrink-0'
              />
            </button>
          </SortableItemHandle>

          <Checkbox
            disabled={!column.getCanHide()}
            checked={column.getIsVisible()}
            onCheckedChange={() => {
              column.toggleVisibility(!column.getIsVisible());
            }}
            id={column.id}
            className='border-primary-800'
          />

          <Label
            htmlFor={column.id}
            title={name}
            className='text-sm text-gray-700 mb-0 line-clamp-1'
          >
            {name}
          </Label>

          {isColumnRenamed && (
            <Badge
              variant='primaryBlue'
              className='rounded-full font-normal'
            >
              Renamed
            </Badge>
          )}

          <Edit2
            onClick={handleEnterEdit}
            className='text-purple-700 hidden group-hover:block cursor-pointer shrink-0'
            size={16}
          />
        </div>

        {(column.getIsPinned() || !isMaximumPinned) && (
          <PinIcon
            size={20}
            onClick={() => {
              if (column.getIsPinned()) {
                column.pin(false);
              } else {
                column.pin('left');
              }
            }}
            className={cn(
              'cursor-pointer text-gray-400 stroke-[1.5px] shrink-0',
              column.getIsPinned() && 'fill-primary-900 text-primary-900'
            )}
          />
        )}
      </li>
    </SortableItem>
  );
};

export const ViewSettings = memo(() => {
  const { table } = useTiclickTableContext();
  const columns = table
    .getAllLeafColumns()
    .filter((column) => column.getCanHide());

  const isMaximumPinned =
    table.options.meta?.maxPinnedColumns ===
    table.options.meta?.getLeafColumnPinnedCount();

  const { currentView } = useTableStates();
  const isDirty = useIsSettingDirty();

  const closeRef = useRef<HTMLButtonElement>(null);

  const tableViewEntityType =
    table.options.meta?.identifyingData.viewEntityType;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <TableActionIconButton
          label='Settings'
          showLabelOnHover={false}
          className='relative bg-white border-primary-200'
          icon={<Setting2 className='text-secondary-1000' />}
        />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>Settings</DialogHeader>
        <DialogBody>
          <Sortable
            value={columns}
            onValueChange={(items) => {
              table.setColumnOrder(items.map((i) => i.id));
            }}
            getItemValue={(col) => col.id}
          >
            <Command className='h-auto'>
              <CommandInput placeholder='Search in columns' />
              <SortableContent>
                <CommandList
                  asChild
                  className='flex flex-col gap-4 mt-2 max-h-min overflow-visible'
                >
                  {columns.map((c) => {
                    return (
                      <CommandItem
                        asChild
                        value={
                          c.columnDef.newHeader ||
                          (c.columnDef.header as string)
                        }
                        key={c.columnDef.id}
                      >
                        <Item
                          column={c}
                          isMaximumPinned={isMaximumPinned}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandList>
              </SortableContent>
            </Command>
          </Sortable>
        </DialogBody>
        {tableViewEntityType && (
          <DialogSub>
            <DialogFooter>
              {currentView?.id ? (
                <div className='flex gap-2 w-full'>
                  <UpdateViewBtn />
                  <ResetToOriginalViewBtn />
                </div>
              ) : (
                <DialogSubTrigger
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                  className='w-full flex-1'
                >
                  <Button
                    disabled={!isDirty}
                    className='flex-1 w-full'
                    size='sm'
                  >
                    Save as new view
                  </Button>
                </DialogSubTrigger>
              )}

              <DialogSubContent>
                <DialogSubHeader>Create new view</DialogSubHeader>
                <div className='p-4'>
                  <CreateOrEditViewForm closeRef={closeRef} />
                </div>
              </DialogSubContent>
            </DialogFooter>
          </DialogSub>
        )}
      </DialogContent>
      <DialogClose ref={closeRef} />
    </Dialog>
  );
});

const UpdateViewBtn = () => {
  const closeRef = useRef<HTMLButtonElement>(null);

  const {
    currentView,
    columnOrder,
    columnPinning,
    columnVisibility,
    columnNames,
  } = useTableStates();

  const { mutate: editView, isPending } = viewApi.useUpdate();

  const isDirty = useIsSettingDirty();

  return (
    <>
      <Button
        className='flex-1 w-full'
        size='sm'
        disabled={!isDirty || isPending}
        onClick={() => {
          if (!currentView?.id) return;

          editView(
            {
              id: currentView?.id,
              visible_static_fields: {
                ...(currentView?.visible_static_fields || {}),
                ordering: columnOrder,
                pinning: columnPinning,
                visibility: columnVisibility,
                names: columnNames || {},
              },
            },
            { onSuccess: () => closeRef.current?.click() }
          );
        }}
      >
        Update View
      </Button>
      <DialogClose
        ref={closeRef}
        className='hidden'
      />
    </>
  );
};

const ResetToOriginalViewBtn = () => {
  const { currentView, setCurrentView } = useTableStates();

  const isDirty = useIsSettingDirty();

  return (
    <Button
      className='flex-1 w-full'
      size='sm'
      variant={'primaryOutline'}
      disabled={!isDirty}
      onClick={() => {
        if (!currentView?.id) return;
        setCurrentView(currentView);
      }}
    >
      Reset
    </Button>
  );
};
